/**
 * Shared constants used across the worker and web app.
 * NOTE: These values are safe to expose to the client. Anything sensitive
 * (model identifiers, cost limits, secrets) lives server-side only.
 */

export const PLAN_LAUNCH_FREE = 'launch_free' as const;

/**
 * Default launch-free entitlement limits. These are *fallbacks*; the
 * authoritative values live in Firestore `systemConfig/public` and are
 * enforced server-side.
 */
export const DEFAULT_LAUNCH_LIMITS = {
  launchFreeEnabled: true,
  launchFreeEndsAt: null as string | null,
  maxAnonymousRunsPerDay: 1,
  maxRegisteredRunsPerDay: 5,
  maxPromptsPerRun: 15,
  maxProjects: 3,
  maxCompetitorsPerProject: 3,
  savedHistoryDays: 90,
  advancedAnalysisAvailable: false,
} as const;

export const ANON_RESULT_TTL_SECONDS = 60 * 60 * 24; // 24 hours

export const PROMPT_CATEGORIES = [
  'discovery',
  'category_recommendation',
  'comparison',
  'problem_solving',
  'high_commercial_intent',
  'branded_audit',
] as const;

export type PromptCategory = (typeof PROMPT_CATEGORIES)[number];

export const ANALYSIS_STATUSES = [
  'created',
  'queued',
  'running',
  'partially_completed',
  'completed',
  'failed',
  'expired',
] as const;

export type AnalysisStatus = (typeof ANALYSIS_STATUSES)[number];

export const PROMPT_RESULT_STATUSES = [
  'pending',
  'running',
  'completed',
  'failed',
  'skipped',
] as const;

export type PromptResultStatus = (typeof PROMPT_RESULT_STATUSES)[number];

export const CONTACT_REASONS = [
  'Advanced Analysis',
  'Agency Access',
  'Business Partnership',
  'Support',
  'Other',
] as const;

export type ContactReason = (typeof CONTACT_REASONS)[number];

/** Human-readable labels for prompt categories used in the UI. */
export const PROMPT_CATEGORY_LABELS: Record<PromptCategory, string> = {
  discovery: 'Discovery',
  category_recommendation: 'Category Recommendation',
  comparison: 'Comparison',
  problem_solving: 'Problem Solving',
  high_commercial_intent: 'High Commercial Intent',
  branded_audit: 'Branded Audit',
};
